import React, { FC, useMemo } from "react";
import { Link } from "react-router-dom";

import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import {
	formatDateTimeCompact,
	nowInTz,
	parseUtc,
} from "@/features/training/lib/trainingDateUtils";
import { SessionType } from "@/features/training/model/Training/TrainingDrill";
import { TrainingDrillSessionSummary } from "@/features/training/model/Training/TrainingDrillSession";
import { TrainingSessionSummary } from "@/features/training/model/Training/TrainingSession";
import CircularProgressWithLabel from "@/shared/ui/CircularProgressWithLabel";
import EngineeringIcon from "@mui/icons-material/Engineering";
import FestivalIcon from "@mui/icons-material/Festival";
import SchoolIcon from "@mui/icons-material/School";
import {
	Avatar,
	Box,
	Card,
	CardActionArea,
	Chip,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface UpcomingSessionItemProps {
	session: TrainingSessionSummary | TrainingDrillSessionSummary;
}

export const UpcomingSessionItem: FC<UpcomingSessionItemProps> = ({
	session,
}) => {
	const theme = useTheme();
	const { timezone } = useTime();

	// Link target
	const linkUrl =
		"CourseId" in session
			? `/training/courses/${session.CourseId}/sessions/${session.Id}`
			: `/training/drills/${session.DrillId}/sessions/${session.Id}`;

	// Derived time info (uses timezone)
	const { daysAway, chip, bandColor, textColor } = useMemo(() => {
		const now = nowInTz(timezone).startOf("day");
		const when = parseUtc(session.Date).tz(timezone).startOf("day");
		const diffDays = when.diff(now, "day"); // can be negative

		// Map to theme tones (previously red/orange/yellow/green)
		let tone: "error" | "warning" | "info" | "success" = "success";
		if (diffDays < 0) tone = "error";
		else if (diffDays <= 1) tone = "warning";
		else if (diffDays <= 7) tone = "info";
		else tone = "success";

		const label =
			diffDays < 0 ? (
				<>
					<Translate String="Training.session.overdue">Overdue by</Translate>
					{" "}
					{Math.abs(diffDays)}
					{" "}
					<Translate String="Common.days">days</Translate>
				</>
			) : diffDays === 0 ? (
				<Translate String="Common.Today">Today</Translate>
			) : diffDays === 1 ? (
				<>
					<Translate String="Common.In">In</Translate>
					{" "}
					{1}
					{" "}
					<Translate String="Common.day">day</Translate>
				</>
			) : (
				<>
					<Translate String="Common.In">In</Translate>
					{" "}
					{diffDays}
					{" "}
					<Translate String="Common.days">days</Translate>
				</>
			);

		const band = theme.palette[tone].main;
		const txt = theme.palette[tone].main;

		return {
			daysAway: diffDays,
			bandColor: band,
			textColor: txt,
			chip: (
				<Chip
					size="small"
					variant="outlined"
					color={tone}
					label={
						<Typography variant="caption" sx={{ lineHeight: 1.2 }}>
							{label}
						</Typography>
					}
					sx={{ borderRadius: 1.5 }}
				/>
			),
		};
	}, [session.Date, timezone, theme.palette]);

	return (
		<Card
			variant="outlined"
			sx={{
				position: "relative",
				borderRadius: 2,
				overflow: "hidden",
				borderColor: theme.palette.divider,
				"&:hover": {
					borderColor: alpha(bandColor, 0.28),
					backgroundColor: alpha(theme.palette.primary.main, 0.02),
				},
				"&::before": {
					content: '""',
					position: "absolute",
					left: 0,
					top: 0,
					bottom: 0,
					width: 3,
					backgroundColor: bandColor,
				},
			}}
		>
			<CardActionArea component={Link} to={linkUrl} sx={{ p: 1.5, pr: 1.75 }}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Avatar
						variant="rounded"
						sx={{
							width: 36,
							height: 36,
							borderRadius: 2,
							border: `1px solid ${alpha(bandColor, 0.16)}`,
							bgcolor: alpha(bandColor, 0.08),
							color: textColor,
						}}
					>
						<SessionIcon session={session} />
					</Avatar>

					{/* Title + time */}
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Tooltip title={session.Name || ""} enterDelay={600}>
							<Typography
								variant="subtitle2"
								fontWeight={700}
								noWrap
								sx={{ color: "text.primary" }}
							>
								{session.Name}
							</Typography>
						</Tooltip>

						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block", mt: 0.25 }}
						>
							{formatDateTimeCompact(session.Date, timezone)}
						</Typography>
					</Box>

					{/* Status area */}
					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						sx={{ ml: 1, flexShrink: 0 }}
					>
						{daysAway < 0 ? (
							// Overdue: just show chip
							<Box>{chip}</Box>
						) : (
							<>
								<Box sx={{ minWidth: 54 }}>
									<CircularProgressWithLabel
										value={daysAway}
										color="secondary"
										type="d"
									/>
								</Box>
								<Box sx={{ display: { xs: "none", sm: "block" } }}>{chip}</Box>
							</>
						)}
					</Stack>
				</Stack>
			</CardActionArea>
		</Card>
	);
};

const SessionIcon: FC<UpcomingSessionItemProps> = ({ session }) => {
	const styles = { fontSize: 20 };
	if ("CourseId" in session) return <SchoolIcon sx={styles} />;
	if (session.Type === SessionType.Exercise)
		return <EngineeringIcon sx={styles} />;
	return <FestivalIcon sx={styles} />;
};
